import { useSearchParams } from "react-router-dom"
import { useCallback, useLayoutEffect, useMemo, useState } from "react"
import { INSTRUMENT_TYPES } from "resources/instruments"

export type UrlInstrumentType = Extract<keyof typeof INSTRUMENT_TYPES, string>

const URL_INSTRUMENT_TYPE_ALIASES: Record<string, UrlInstrumentType> = {
  asset: "etf",
  swap: "basket",
}

export function normalizeUrlInstrumentType(type: string | null): UrlInstrumentType | null {
  if (!type) {
    return null
  }

  const normalizedType = type.toLowerCase()

  if (normalizedType in INSTRUMENT_TYPES) {
    return normalizedType as UrlInstrumentType
  }

  const alias = URL_INSTRUMENT_TYPE_ALIASES[normalizedType]
  if (alias) {
    return alias
  }

  const matchByInstrumentTypeValue = (Object.keys(INSTRUMENT_TYPES) as UrlInstrumentType[]).find(
    (instrumentType) => INSTRUMENT_TYPES[instrumentType].toLowerCase() === normalizedType,
  )

  return matchByInstrumentTypeValue ?? null
}

export interface UrlInstrument {
  urlInstrumentType: UrlInstrumentType | null
  urlInstrumentId: string | null
}

interface InstrumentParams {
  instrument: UrlInstrument
  clearInstrumentFromUrl: () => void
}

const EMPTY: UrlInstrument = {
  urlInstrumentType: null,
  urlInstrumentId: null,
}

export function useGetUrlInstrumentParams(): InstrumentParams {
  const [searchParams, setSearchParams] = useSearchParams()
  const [lastValid, setLastValid] = useState<UrlInstrument>(EMPTY)

  const derivedFromUrl = useMemo(() => {
    const type = searchParams.get("instrumentType")
    const id = searchParams.get("instrumentId")
    const urlInstrumentType = normalizeUrlInstrumentType(type)
    if (id && urlInstrumentType) {
      return { urlInstrumentType, urlInstrumentId: id }
    }
    return null
  }, [searchParams])

  const instrument = derivedFromUrl ?? lastValid

  // Stash params before any effect clears them from the URL (SPA navigation, Strict Mode remount).
  useLayoutEffect(() => {
    if (derivedFromUrl) {
      setLastValid(derivedFromUrl)
    }
  }, [derivedFromUrl])

  const clearInstrumentFromUrl = useCallback(() => {
    const type = searchParams.get("instrumentType")
    const id = searchParams.get("instrumentId")
    if (!type && !id) return
    const urlInstrumentType = normalizeUrlInstrumentType(type)
    if (id && urlInstrumentType) {
      setLastValid({ urlInstrumentType, urlInstrumentId: id })
    } else {
      setLastValid(EMPTY)
    }
    const newParams = new URLSearchParams(searchParams)
    newParams.delete("instrumentType")
    newParams.delete("instrumentId")
    setSearchParams(newParams, { replace: true })
  }, [searchParams, setSearchParams])

  return { instrument, clearInstrumentFromUrl }
}
